// Client for the master-data (Stammdaten) Excel import/export endpoints.
// This file exists as an identical copy in web/auftragsbuch and web/zeitkontrolle;
// keep both copies byte-identical. The API prefix is passed in by the caller:
//   auftragsbuch:  Server_URL + '/auftragsbuch/basis_data/io'
//   zeitkontrolle: Server_URL_ZK + '/zk/basis_data/io'
import Vue from 'vue';
import {Redirect_Login} from '@/shared/utils';

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const GENERIC_ERROR = 'Der Server ist nicht erreichbar oder hat einen Fehler gemeldet. Bitte versuchen Sie es später erneut.';

const STATUS_MESSAGES = {
  400: 'Die Anfrage ist ungültig.',
  404: 'Die hochgeladene Datei wurde nicht gefunden oder ist abgelaufen. Bitte laden Sie die Datei erneut hoch.',
  409: 'Der Vorgang steht im Konflikt mit einem anderen Import. Bitte prüfen Sie die Datei erneut.',
  413: 'Die Datei ist zu groß.',
  422: 'Die Daten konnten nicht verarbeitet werden.',
};

function profileUrl(apiPrefix, profile, action) {
  return apiPrefix + '/' + encodeURIComponent(profile) + '/' + action;
}

export function getProfile(apiPrefix, profile) {
  return Vue.http.get(profileUrl(apiPrefix, profile, 'profile'));
}

export function upload(apiPrefix, profile, file) {
  const form = new FormData();
  form.append('upload_file', file, file.name);
  return Vue.http.post(profileUrl(apiPrefix, profile, 'upload'), form);
}

export function preview(apiPrefix, profile, request) {
  return Vue.http.post(profileUrl(apiPrefix, profile, 'preview'), request);
}

export function validate(apiPrefix, profile, request) {
  return Vue.http.post(profileUrl(apiPrefix, profile, 'validate'), request);
}

export function doImport(apiPrefix, profile, request) {
  return Vue.http.post(profileUrl(apiPrefix, profile, 'import'), request);
}

// error report of the last validation of a staged upload
export function downloadReport(apiPrefix, profile, token) {
  return download(profileUrl(apiPrefix, profile, 'report'), {token: token}, 'Fehlerbericht_' + profile + '.xlsx');
}

// kind: template | data | audit
export function downloadExport(apiPrefix, profile, kind) {
  return download(profileUrl(apiPrefix, profile, 'export'), {kind: kind}, profile + '_' + kind + '.xlsx');
}

// error report stored with an import audit row
export function downloadAuditReport(apiPrefix, id) {
  return download(apiPrefix + '/report/' + encodeURIComponent(id), {}, 'Fehlerbericht_' + id + '.xlsx');
}

function download(url, params, fallbackName) {
  return Vue.http.get(url, {params: params, responseType: 'blob'}).then(response => {
    saveBlob(response.body, fileNameFrom(response, fallbackName));
    return response;
  });
}

// fileNameFrom reads the file name from the Content-Disposition header
// (the server sets Access-Control-Expose-Headers so it is readable cross-origin).
export function fileNameFrom(response, fallbackName) {
  const disposition = response && response.headers ? response.headers.get('Content-Disposition') : '';
  if (disposition) {
    const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;\n]+)/.exec(disposition);
    if (encoded && encoded[1]) {
      try {
        return decodeURIComponent(encoded[1].trim().replace(/['"]/g, ''));
      } catch (e) {
        // fall through to the plain filename
      }
    }
    const plain = /filename\s*=\s*((['"]).*?\2|[^;\n]*)/.exec(disposition);
    if (plain && plain[1]) {
      const name = plain[1].replace(/['"]/g, '').trim();
      if (name) {
        return name;
      }
    }
  }
  return fallbackName;
}

function saveBlob(body, fileName) {
  const blob = body instanceof Blob ? body : new Blob([body], {type: XLSX_TYPE});
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // revoke on the next tick, some browsers start the download asynchronously
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// errorMessage turns a failed request into a German message for an inline alert.
// 401 redirects to the login page and resolves to ''. It never redirects to page500.
export function errorMessage(response) {
  if (!response || typeof response.status !== 'number') {
    console.error(response);
    return Promise.resolve(GENERIC_ERROR);
  }
  const status = response.status;
  if (status === 401) {
    window.location = Redirect_Login;
    return Promise.resolve('');
  }
  if (status === 403) {
    return Promise.resolve('Keine Berechtigung');
  }
  if (status >= 400 && status < 500) {
    return readErrorBody(response.body).then(text => text || STATUS_MESSAGES[status] || 'Die Anfrage konnte nicht verarbeitet werden.');
  }
  return Promise.resolve(GENERIC_ERROR);
}

// JSON error bodies of blob requests may still arrive as Blob.
function readErrorBody(body) {
  if (!body) {
    return Promise.resolve('');
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return blobText(body).then(parseErrorText, () => '');
  }
  if (typeof body === 'string') {
    return Promise.resolve(parseErrorText(body));
  }
  return Promise.resolve(typeof body.error === 'string' ? body.error : '');
}

function parseErrorText(text) {
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed.error === 'string' ? parsed.error : '';
  } catch (e) {
    return '';
  }
}

function blobText(blob) {
  if (typeof blob.text === 'function') {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

// formatMessage renders a Message (sheet, row, column, text) of an upload,
// validation or import as one line, e.g. "Blatt „LEAR“, Zeile 4: ...".
export function formatMessage(message) {
  if (!message) {
    return '';
  }
  if (typeof message === 'string') {
    return message;
  }
  const parts = [];
  if (message.sheet) {
    parts.push('Blatt „' + message.sheet + '“');
  }
  if (message.row) {
    parts.push('Zeile ' + message.row);
  }
  if (message.column) {
    parts.push('Spalte ' + message.column);
  }
  return (parts.length ? parts.join(', ') + ': ' : '') + (message.text || '');
}

export function formatBytes(bytes) {
  if (!bytes || bytes < 1024) {
    return (bytes || 0) + ' B';
  }
  if (bytes < 1024 * 1024) {
    return Math.round(bytes / 1024) + ' KB';
  }
  return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',') + ' MB';
}
